/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CONTRACT_STATUS_LABEL } from '@kd/shared';
import { PartyPicker } from '@/components/PartyPicker';
import { Badge, Button, Empty, ErrorText, Field, Input, Modal, PageHeader, Pager, Select, Spinner } from '@/components/ui';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtMoney, today } from '@/lib/format';
import { useDebounced } from '@/lib/hooks';

export const statusTone = (s: string) => ({ ACTIVE: 'green', DRAFT: 'slate', APPROVAL: 'amber', CLOSED: 'blue', TERMINATED: 'red' } as const)[s as 'ACTIVE'] ?? 'slate';

export function ContractsPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [expiring, setExpiring] = useState(false);
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const dq = useDebounced(q);
  const { data, isLoading } = useQuery({
    queryKey: ['contracts', dq, status, expiring, page],
    queryFn: () => api.get(`/contracts${qs({ q: dq, status, expiringDays: expiring ? 30 : undefined, page, pageSize: 50 })}`),
  });
  return (
    <>
      <PageHeader title="Договоры" actions={can('contract.write') && <Button onClick={() => setCreating(true)}>Новый договор</Button>} />
      <div className="card">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 p-3">
          <Input className="max-w-xs" placeholder="Номер, предмет, контрагент" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <Select className="max-w-[180px]" value={status} onChange={(e) => setStatus(e.target.value)} placeholder="Все статусы"
            options={Object.entries(CONTRACT_STATUS_LABEL).map(([value, label]) => ({ value, label }))} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={expiring} onChange={(e) => setExpiring(e.target.checked)} /> Истекают в ближайшие 30 дней</label>
        </div>
        {isLoading ? <Spinner /> : !data?.items.length ? <Empty>Договоров нет</Empty> : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-slate-50"><tr><th className="th">Договор</th><th className="th">Контрагент</th><th className="th">Предмет</th><th className="th text-right">Сумма</th><th className="th">Окончание</th><th className="th">Статус</th></tr></thead>
            <tbody>{data.items.map((c: any) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="td whitespace-nowrap"><Link className="font-medium text-brand-700 hover:underline" to={`/contracts/${c.id}`}>№ {c.number}</Link>
                  <div className="text-xs text-slate-500">от {fmtDate(c.date)} · {c.kind === 'CLIENT' ? 'с клиентом' : 'с поставщиком'}</div></td>
                <td className="td">{c.partyName}</td><td className="td">{c.subject}</td>
                <td className="td text-right tabular-nums">{fmtMoney(c.amount)}</td><td className="td">{fmtDate(c.endDate)}</td>
                <td className="td"><Badge tone={statusTone(c.status)}>{CONTRACT_STATUS_LABEL[c.status]}</Badge></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
        {data && <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />}
      </div>
      <Modal title="Новый договор" open={creating} onClose={() => setCreating(false)} wide>
        <ContractForm onSaved={(c) => nav(`/contracts/${c.id}`)} />
      </Modal>
    </>
  );
}

export function ContractForm({ contract, onSaved }: { contract?: any; onSaved: (c: any) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    number: contract?.number ?? '', date: contract?.date ?? today(), kind: contract?.kind ?? 'CLIENT', partyId: contract?.partyId ?? null,
    partyName: contract?.party?.name ?? '', subject: contract?.subject ?? '', amount: contract?.amount ?? '', startDate: contract?.startDate ?? '',
    endDate: contract?.endDate ?? '', autoRenew: contract?.autoRenew ?? false,
  });
  const save = useMutation({
    mutationFn: () => {
      const { partyName, ...rest } = f;
      const body = { ...rest, amount: f.amount || null, startDate: f.startDate || null, endDate: f.endDate || null };
      return contract ? api.patch(`/contracts/${contract.id}`, body) : api.post('/contracts', body);
    },
    onSuccess: (c) => { qc.invalidateQueries({ queryKey: ['contracts'] }); qc.invalidateQueries({ queryKey: ['contract'] }); onSaved(c); },
  });
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Номер"><Input required value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} /></Field>
        <Field label="Дата"><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Вид"><Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} options={[{ value: 'CLIENT', label: 'С клиентом' }, { value: 'SUPPLIER', label: 'С поставщиком' }]} /></Field>
      </div>
      <Field label="Контрагент"><PartyPicker value={f.partyId} valueLabel={f.partyName} onChange={(id, n) => setF({ ...f, partyId: id, partyName: n ?? '' })} /></Field>
      <Field label="Предмет"><Input required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></Field>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Сумма, ₽"><Input inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value.replace(',', '.') })} /></Field>
        <Field label="Начало"><Input type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} /></Field>
        <Field label="Окончание"><Input type="date" value={f.endDate} onChange={(e) => setF({ ...f, endDate: e.target.value })} /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.autoRenew} onChange={(e) => setF({ ...f, autoRenew: e.target.checked })} /> Автопролонгация</label>
      <ErrorText error={save.error} />
      <div className="flex justify-end"><Button type="submit" disabled={!f.partyId || save.isPending}>Сохранить</Button></div>
    </form>
  );
}
